import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';

import { Strings } from '@app/constants';
import { FormProvider, RHFSelect, RHFTextField } from '@app/components';
import { Header } from '@app/sections/header';
import { IAssets } from '../types';
import { addRepairSchema } from '../validators';
import { addRepairAssets } from '../slice';
import { useRequestState } from '@app/hooks';
import { selectAddRepairAssests } from '../selectors';

const AddRepair = ({
  open,
  onClose,
  row,
}: {
  open: boolean;
  onClose: VoidFunction;
  row: IAssets;
}) => {
  const dispatch = useDispatch();
  const lastRepair = row.repair?.[0] ?? {};
  const {
    partName: lastPartName,
    status: lastStatus,
    about: lastAbout,
    repairCost: lastCost,
  } = lastRepair;

  const methods = useForm<IAssets>({
    resolver: yupResolver(addRepairSchema) as any,
    defaultValues: {
      repairPart: lastPartName,
      status: lastStatus || 'Repairing',
      about: lastAbout,
      cost: lastCost,
    },
  });

  const { loading } = useRequestState({
    stateSelector: selectAddRepairAssests,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  const onSubmit = (form: IAssets) => {
    console.log('form', form);
    dispatch(addRepairAssets({ ...form, assetId: row?.id }));
  };

  return (
    <FormProvider methods={methods}>
      <Dialog open={open} fullWidth>
        <Header variant="dialog" title={Strings.assets.repair} onClose={onClose} />
        <Divider />
        <DialogContent>
          <Stack spacing={2} my={2}>
            <RHFTextField name="repairPart" required label={Strings.assets.repairPart} fullWidth />

            <RHFSelect
              required
              name="status"
              type="select"
              label={Strings.assets.status}
              disabled={!lastStatus}
            >
              {['Repairing', 'Repaired', 'Assigned'].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </RHFSelect>
            {lastStatus && (
              <RHFTextField name="cost" label={Strings.assets.cost} fullWidth variant="outlined" />
            )}
            <RHFTextField
              name="about"
              label={Strings.field.about}
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {Strings.button.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={loading}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {Strings.button.save}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export { AddRepair };
